import React, { useEffect, useMemo } from 'react';
import { Navbar, Nav, NavDropdown } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { LinkContainer } from 'react-router-bootstrap';
import { useTranslation } from 'react-i18next';
import { useFirebase } from '../../hooks/firebase';
import * as ROUTES from '../../constants/routes';
import DefaultLogo from '../../assets/images/Logo-ONE.png';
import { useFreshchat } from '../../hooks/freshchat';
import {
	CERTIFICATES_ENABLED,
	COURSE_AGENDA_ENABLED,
	useViewPreferences,
} from '../../hooks/view-preferences';
import { useUser } from '../../hooks/use-user';
import useApi from '../../hooks/use-api';
import { GET_LOGO } from '../../constants/routes';

function TopNavBar() {
	const firebase = useFirebase();
	const navigate = useNavigate();
	const { t } = useTranslation();
	const { toggleChat, destroyFreshchat } = useFreshchat();
	const { isPreferenceOptionEnabled } = useViewPreferences();
	const {
		profile,
		activeCompany,
		setCompany,
		isCompanyAdmin,
		isCompanySuperUser,
		isSchoolAdmin,
		isSchoolSuperUser,
		isPortalAdmin,
	} = useUser();

	const [{ data: logo }, fetchLogo] = useApi.get(GET_LOGO, {}, { lazy: true });

	useEffect(() => {
		fetchLogo();
	}, []);

	const siteLogo = useMemo(() => {
		let src = DefaultLogo;

		if (logo?.fileContent) {
			src = `data:image/jpeg;base64,${logo.fileContent}`;
		}
		return <img src={src} className="logo-image" alt={t('portal.name')} />;
	}, [logo]);

	const settings = { id: 'Settings', link: ROUTES.SETTINGS };
	const companyAdminMenuOptions = [
		{ name: t('navigation.companyUser.dashboard'), link: ROUTES.DASHBOARD, id: 'dashboardItem' },
		{ name: t('navigation.companyUser.employees'), link: ROUTES.EMPLOYEES, id: 'employeeItem' },
		...(isPreferenceOptionEnabled(COURSE_AGENDA_ENABLED)
			? [{ name: t('navigation.companyUser.offer'), link: ROUTES.INVENTORY, id: 'offerItem' }]
			: []),
		{
			name: t('navigation.companyUser.planning'),
			link: ROUTES.COURSE_PLANNING,
			id: 'planningItem',
		},
		...(isPreferenceOptionEnabled(CERTIFICATES_ENABLED)
			? [
					{
						name: t('navigation.companyUser.certificates'),
						link: ROUTES.CERTIFICATES,
						id: 'certificates',
					},
				]
			: []),
	];

	const schoolAdminMenuVariables = {
		menu_options: [{ name: t('navigation.dationUser.companies'), link: ROUTES.COMPANIES }],
		impersonation_options: companyAdminMenuOptions,
		shouldUseFreshchat: true,
		settings_option: settings,
	};

	const menuVariablesMapping = () => {
		if (isCompanyAdmin) {
			return { menu_options: companyAdminMenuOptions };
		}
		if (isCompanySuperUser) {
			return {
				menu_options: companyAdminMenuOptions,
				settings_option: settings,
			};
		}
		if (isSchoolAdmin || isSchoolSuperUser) {
			return schoolAdminMenuVariables;
		}
		if (isPortalAdmin) {
			return {
				menu_options: [
					{ name: t('navigation.adminUser.dation_instances'), link: ROUTES.DATION_INSTANCES },
					{ name: t('navigation.adminUser.statistics'), link: ROUTES.PORTAL_STATISTICS },
				],
			};
		}
		return null;
	};

	const user = firebase.authUser;
	const { companies = [] } = profile || {};

	const menuVariables = menuVariablesMapping();
	if (menuVariables === null) {
		return null;
	}
	const {
		menu_options: menuOptions = [],
		impersonation_options: impersonationOptions = [],
		settings_option: settingsOption = null,
		shouldUseFreshchat = false,
	} = menuVariables || {};

	const onSignOut = () => {
		firebase.doSignOut().then(() => {
			navigate(ROUTES.LOGIN);
			setCompany(null);
			destroyFreshchat();
		});
	};

	return (
		<Navbar collapseOnSelect expand="lg" className="top-navbar bg-body-tertiary">
			<Navbar.Brand className="custom-brand" href="#">
				{siteLogo}
			</Navbar.Brand>
			<Navbar.Toggle aria-controls="responsive-navbar-nav" />
			<Navbar.Collapse id="responsive-navbar-nav">
				<Nav className="d-flex flex-collapse justify-content-between w-100">
					<div className="d-flex flex-collapse">
						{menuOptions.map((item) => (
							<LinkContainer
								style={{ fontSize: '1.20rem' }}
								to={item.link}
								key={item.name}
								id={item.id}>
								<Nav.Link>{item.name}</Nav.Link>
							</LinkContainer>
						))}
						{activeCompany?.name && impersonationOptions.length > 0 && (
							<div className="mx-3 my-0 border-end border-primary border-3" />
						)}
						{activeCompany?.name &&
							impersonationOptions.map((item) => (
								<LinkContainer
									style={{ fontSize: '1.20rem' }}
									to={item.link}
									key={item.name}
									id={item.id}>
									<Nav.Link>{item.name}</Nav.Link>
								</LinkContainer>
							))}
					</div>
					<div className="d-flex flex-collapse flex-collapse-center">
						{(isSchoolAdmin || isSchoolSuperUser) && activeCompany && (
							<div>{activeCompany?.name}</div>
						)}
						{(isCompanyAdmin || isCompanySuperUser) && companies.length > 1 && (
							<div className="d-flex">
								<select
									className="form-select"
									defaultValue={activeCompany.id}
									onChange={({ target }) =>
										setCompany(
											companies.find((company) => company.id === parseInt(target.value, 10)),
										)
									}>
									{companies
										.sort((companyA, companyB) => companyA.name.localeCompare(companyB.name))
										.map((currentCompany) => (
										<option key={currentCompany.id} value={currentCompany.id}>
											{currentCompany.name}
										</option>
									))}
								</select>
							</div>
						)}
						{shouldUseFreshchat ? (
							<span
								id="freshchatButton"
								title={t('portal.chat_with_support')}
								className="glyphicons glyphicons-user-chat glyphicons-navbar-icon nav-link"
								onClick={toggleChat}
							/>
						) : (
							''
						)}
						{settingsOption && (
							<LinkContainer id="settingsLink" to={settingsOption.link}>
								<Nav.Link>
									<span className="glyphicons glyphicons-cogwheel glyphicons-navbar-icon d-grid" />
								</Nav.Link>
							</LinkContainer>
						)}
						<NavDropdown
							align="end"
							title={
								<span className="glyphicons glyphicons-user-rounded  glyphicons-navbar-icon" />
							}
							id="basic-nav-dropdown"
							className="navbar-dropdown custom-navbar-dropdown">
							{user.displayName && <NavDropdown.Item>{user.displayName}</NavDropdown.Item>}
							{user.email && <NavDropdown.Item>{user.email}</NavDropdown.Item>}
							{profile?.company && <NavDropdown.Item>{profile.company.name}</NavDropdown.Item>}
							<NavDropdown.Divider />
							<NavDropdown.Item id="logout" onClick={onSignOut}>
								{t('portal.logout')}
							</NavDropdown.Item>
						</NavDropdown>
					</div>
				</Nav>
			</Navbar.Collapse>
		</Navbar>
	);
}

export default TopNavBar;
